import { isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { ClientSettings, ClientTheme } from '../models/client-settings.model';
import { AuthService } from './auth.service';
import { ClientSettingsService } from './client-settings.service';

@Injectable({
  providedIn: 'root',
})
export class ClientThemeService {
  private readonly clientSettingsService = inject(ClientSettingsService);

  private readonly authService = inject(AuthService);

  private readonly platformId = inject(PLATFORM_ID);

  private readonly _loadedClientId = signal<string | null>(null);

  constructor() {
    effect(() => {
      const clientId = this.authService.currentUser()?.clientId?.trim() ?? '';

      if (clientId && clientId !== this._loadedClientId()) {
        this.loadAndApply(clientId);
        return;
      }

      if (!clientId && this._loadedClientId()) {
        this.resetToDefault();
      }
    });

    /**
     * O ClientSettingsService atualiza o signal
     * settings após salvar o tema e as cores.
     *
     * Este effect aplica o novo valor imediatamente,
     * sem exigir recarregamento da página.
     */
    effect(() => {
      const settings = this.clientSettingsService.settings();

      const authenticatedClientId = this.authService.currentUser()?.clientId?.trim() ?? '';

      if (!settings || !authenticatedClientId) {
        return;
      }

      const settingsClientId = settings.clientId?.trim() ?? '';

      /**
       * Evita aplicar na sessão atual o tema de outro
       * cliente que esteja sendo editado.
       */
      if (settingsClientId && settingsClientId !== authenticatedClientId) {
        return;
      }

      this._loadedClientId.set(authenticatedClientId);

      this.applyTheme(settings);
    });
  }

  private get root(): HTMLElement {
    return document.documentElement;
  }

  async loadAndApply(clientId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const normalizedClientId = clientId.trim();

    if (!normalizedClientId) {
      return;
    }

    const settings = await this.clientSettingsService.getByClientId(normalizedClientId);

    this._loadedClientId.set(normalizedClientId);

    if (settings) {
      this.applyTheme(settings);
    }
  }

  applyTheme(settings: ClientSettings): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.applyColorMode(settings.defaultTheme);

    this.applyPrimaryColor(settings.primaryColor);

    this.applyOptionalColor('--color-secondary', settings.secondaryColor);

    this.applyOptionalColor('--color-accent', settings.accentColor);

    this.applyOptionalColor('--color-badge-default', settings.accentColor);
  }

  resetToDefault(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const properties = [
      '--color-primary',
      '--color-primary-dark',
      '--color-primary-darker',
      '--color-secondary',
      '--color-accent',
      '--color-badge-default',
      '--color-background',
      '--color-surface',
      '--color-surface-light',
      '--color-surface-lighter',
      '--color-off-white',
      '--color-text-secondary',
    ];

    properties.forEach((property) => {
      this.root.style.removeProperty(property);
    });

    delete this.root.dataset['theme'];

    this._loadedClientId.set(null);
  }

  private applyColorMode(mode: ClientTheme): void {
    switch (mode) {
      case ClientTheme.Light:
        this.root.dataset['theme'] = 'light';
        break;

      case ClientTheme.Dark:
        this.root.dataset['theme'] = 'dark';
        break;

      case ClientTheme.Default:
      default:
        /**
         * Volta a usar o tema padrão global, em vez
         * de manter light/dark aplicado anteriormente.
         */
        delete this.root.dataset['theme'];
        break;
    }
  }

  private applyPrimaryColor(hex: string | null | undefined): void {
    const normalizedHex = hex?.trim() ?? '';

    if (!normalizedHex) {
      this.root.style.removeProperty('--color-primary');

      this.root.style.removeProperty('--color-primary-dark');

      this.root.style.removeProperty('--color-primary-darker');

      return;
    }

    this.root.style.setProperty('--color-primary', normalizedHex);

    this.root.style.setProperty('--color-primary-dark', this.darken(normalizedHex, 0.12));

    this.root.style.setProperty('--color-primary-darker', this.darken(normalizedHex, 0.25));
  }

  private applyOptionalColor(property: string, value: string | null | undefined): void {
    const normalizedValue = value?.trim() ?? '';

    if (!normalizedValue) {
      this.root.style.removeProperty(property);
      return;
    }

    this.root.style.setProperty(property, normalizedValue);
  }

  private darken(hex: string, factor: number): string {
    const color = hex.replace('#', '');

    const normalizedColor =
      color.length === 3
        ? color
            .split('')
            .map((character) => character + character)
            .join('')
        : color;

    const numericColor = Number.parseInt(normalizedColor, 16);

    const red = Math.max(0, Math.round(((numericColor >> 16) & 0xff) * (1 - factor)));

    const green = Math.max(0, Math.round(((numericColor >> 8) & 0xff) * (1 - factor)));

    const blue = Math.max(0, Math.round((numericColor & 0xff) * (1 - factor)));

    return `#${[red, green, blue].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
  }
}
